import { useEffect, useRef } from "react";
// Types only. The runtime cannot be imported: MediaPipe's ESM and CJS bundles
// both contain `import(t.toString())` — a dynamic import with a computed
// specifier — which Metro refuses to transform ("Invalid call"). A type-only
// import is erased before resolution, so the bundler never opens the file.
import type { FilesetResolver, PoseLandmarker } from "@mediapipe/tasks-vision";

import type { PoseReading } from "../pose/types";

/**
 * The library, once it has loaded itself.
 *
 * MediaPipe also ships a classic-script build that assigns a `Vision` global,
 * and that is the one used here — served from our own origin and loaded by a
 * script tag, which keeps the whole thing outside the bundler. The shape below
 * is the part of it this file touches, typed from the package's own
 * declarations so it cannot drift from the library it describes.
 */
type VisionGlobal = {
  FilesetResolver: typeof FilesetResolver;
  PoseLandmarker: typeof PoseLandmarker;
};

declare global {
  // Declared rather than cast: the script tag below really does define this,
  // and describing it is the honest way to say so.
  interface Window {
    Vision?: VisionGlobal;
  }
}

/**
 * Whether this platform's preview can report a pose at all.
 *
 * Declared per platform rather than inferred from silence, because the two are
 * not the same and the difference is what a patient is told. Web can detect;
 * native cannot, and saying "we can't see you yet" to someone on a phone —
 * forever, over a working picture — is the overlay asserting an observation it
 * never made.
 */
export const PREVIEW_DETECTS_POSE = true;

const BUNDLE_URL = "/mediapipe/vision_bundle.js";

let loading: Promise<VisionGlobal> | null = null;

/**
 * Loads the library once per page, however many times the camera is opened.
 *
 * The promise is cached rather than the element: two stages mounting together
 * would otherwise each append a tag and race, and a second copy of a
 * WebAssembly runtime is an expensive way to get the same object.
 */
function loadVision(): Promise<VisionGlobal> {
  if (loading) return loading;

  loading = new Promise<VisionGlobal>((resolve, reject) => {
    if (window.Vision) {
      resolve(window.Vision);
      return;
    }

    const script = document.createElement("script");
    script.src = BUNDLE_URL;
    script.async = true;
    script.onload = () => {
      if (window.Vision) resolve(window.Vision);
      else reject(new Error("the vision bundle loaded but defined no global"));
    };
    script.onerror = () => {
      // Almost always the assets not being synced: `npm run sync:pose-assets`.
      reject(new Error(`could not load ${BUNDLE_URL}`));
    };
    document.head.appendChild(script);
  }).catch((error: unknown) => {
    // Let a later attempt retry rather than caching the failure forever.
    loading = null;
    throw error;
  });

  return loading;
}

/**
 * The web preview, which owns its stream so a pose model can see the frames.
 *
 * `expo-camera` renders a perfectly good `<video>` here, and it is unreachable:
 * there is no ref to it, and reaching into another component's DOM to find one
 * would make this feature's correctness depend on a detail of someone else's
 * render tree. So this half opens the stream itself. The native half keeps
 * `expo-camera`, because it has no frames to offer either way.
 *
 * ### What runs, and where
 *
 * MediaPipe's pose landmarker, in WebAssembly, on this device. The model and
 * its runtime are served from our own origin — `scripts/sync-pose-assets.mjs`
 * puts them there, the model pinned by digest — rather than from Google's CDN,
 * because a health surface should not be making third-party requests at the
 * moment a patient turns on her camera.
 *
 * **No frame leaves this component.** Landmarks are computed here and only four
 * of them are passed on, as coordinates. Nothing is recorded, uploaded or kept,
 * which is what the disclosure promises and what `no-retention.test.ts` holds
 * this file to.
 *
 * ### What the landmarks are allowed to decide
 *
 * Where to draw. Nothing else. #2267 forbids a general-purpose model deciding
 * whether an injection is safe or has happened, and a pose model asked "is her
 * angle right" would be exactly that — so the only consumer is `pose/zones.ts`,
 * which returns coordinates and has no other output.
 */

/**
 * MediaPipe's pose landmark indices, named.
 *
 * Both of each pair, because every position the coach derives is a midpoint and
 * a midpoint is unchanged by mirroring. Nothing downstream reads a side.
 */
const LEFT_SHOULDER = 11;
const RIGHT_SHOULDER = 12;
const LEFT_HIP = 23;
const RIGHT_HIP = 24;
// Read for the thigh outline. The model returns them whether or not anything
// uses them, so asking costs nothing; `PoseReading` marks them optional because
// the native detector has no equivalent.
const LEFT_KNEE = 25;
const RIGHT_KNEE = 26;

/**
 * Detections per second.
 *
 * Twelve, not sixty. The outline is smoothed and she is barely moving — the
 * phone is propped because she needs both hands — so a faster loop buys nothing
 * visible and spends battery at the moment she is least able to plug in.
 */
const DETECTIONS_PER_SECOND = 12;

export function CameraPreview({
  onMountError,
  onReading,
  onDetectorUnavailable,
}: {
  onMountError: () => void;
  onReading?: (reading: PoseReading, frameAspect: number) => void;
  /** The pose model could not start. The camera is unaffected. */
  onDetectorUnavailable?: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  // Held in a ref so the detection loop does not restart every time the parent
  // re-renders with a new callback identity.
  const onReadingRef = useRef(onReading);
  onReadingRef.current = onReading;
  const onMountErrorRef = useRef(onMountError);
  onMountErrorRef.current = onMountError;
  const onDetectorUnavailableRef = useRef(onDetectorUnavailable);
  onDetectorUnavailableRef.current = onDetectorUnavailable;

  useEffect(() => {
    let cancelled = false;
    let stream: MediaStream | null = null;
    let landmarker: PoseLandmarker | null = null;
    let frame = 0;
    let lastDetection = 0;
    /** The stream is still attached but is delivering no new frames. */
    let blind = false;

    const stop = () => {
      cancelled = true;
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
      stream?.getTracks().forEach((track) => track.stop());
      stream = null;
      landmarker?.close();
      landmarker = null;
    };

    const detect = (now: number) => {
      if (cancelled) return;
      frame = requestAnimationFrame(detect);

      const video = videoRef.current;
      if (!landmarker || !video || video.readyState < 2) return;
      if (now - lastDetection < 1000 / DETECTIONS_PER_SECOND) return;
      lastDetection = now;

      // A frozen frame still detects perfectly. Report nothing rather than
      // report a body that may have moved since the picture stopped.
      if (blind) {
        onReadingRef.current?.(null, 0);
        return;
      }

      let reading: PoseReading = null;
      try {
        const result = landmarker.detectForVideo(video, now);
        const points = result.landmarks?.[0];
        if (points) {
          const leftShoulder = points[LEFT_SHOULDER];
          const rightShoulder = points[RIGHT_SHOULDER];
          const leftHip = points[LEFT_HIP];
          const rightHip = points[RIGHT_HIP];
          if (leftShoulder && rightShoulder && leftHip && rightHip) {
            reading = {
              leftShoulder,
              rightShoulder,
              leftHip,
              rightHip,
              // Passed through as the detector gave them, missing included: a
              // seated patient often has one knee out of frame, and "no knee"
              // is a truthful answer that `injectionZonesFrom` knows to refuse.
              leftKnee: points[LEFT_KNEE],
              rightKnee: points[RIGHT_KNEE],
            };
          }
        }
      } catch {
        // A dropped frame is not worth reporting. `trackZones` treats a missing
        // reading as "draw it where it always was", which is the same thing the
        // next frame will correct.
        reading = null;
      }

      // Read from the element every time rather than cached: a stream can
      // renegotiate its resolution, and an aspect ratio that was right once is
      // not a fact about the session.
      const frameAspect =
        video.videoWidth > 0 && video.videoHeight > 0
          ? video.videoWidth / video.videoHeight
          : 0;
      onReadingRef.current?.(reading, frameAspect);
    };

    /**
     * The camera, and nothing else.
     *
     * Separated from the detector below because they fail for unrelated
     * reasons and used to share an answer. A missing model file — the assets
     * not synced, a bad deploy — ran the same catch as a refused camera, which
     * called `stop()` on a stream that was already open and working and then
     * told the stage to take itself down. One 404 removed the mirror that
     * shipped before any of this, for the rest of her session.
     */
    const startCamera = async () => {
      // Audio explicitly refused. This build declares no microphone purpose
      // string and has no use for one; asking would prompt her for a permission
      // nothing consumes.
      stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
        audio: false,
      });
      if (cancelled) {
        stream.getTracks().forEach((track) => track.stop());
        stream = null;
        return;
      }
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }

      /**
       * A camera that goes away without the page noticing.
       *
       * The `<video>` keeps its last decoded frame and keeps reporting
       * `readyState >= 2`, so without this the detector goes on finding a
       * torso in a still picture and the outline stays drawn, confidently, on
       * a body from several seconds ago. That is the one path that gets past
       * the observation rule the rest of this package is built on — every
       * other way of losing her produces no landmarks.
       *
       * `mute` is the recoverable one: another app or tab took the camera, and
       * it may hand it back. Readings stop, which every consumer already
       * treats as "she is not in frame". `ended` is not recoverable — the
       * device was unplugged, or the permission was revoked mid-walk — so it
       * takes the same exit as a camera that never started.
       */
      const [track] = stream.getVideoTracks();
      track?.addEventListener("mute", () => {
        blind = true;
      });
      track?.addEventListener("unmute", () => {
        blind = false;
      });
      track?.addEventListener("ended", () => {
        if (cancelled) return;
        stop();
        onMountErrorRef.current();
      });
    };

    /**
     * The detector, which is allowed to fail without taking the mirror with it.
     *
     * When it does, no reading is ever reported, and every consumer already
     * treats that as "no outline" — the same state as a patient out of frame,
     * and the same state native is in permanently. She still sees herself, and
     * the words above the picture still carry the step.
     */
    const startDetector = async () => {
      const vision = await loadVision();
      if (cancelled) return;

      // Served from our own origin, never a CDN. See the note above.
      const fileset =
        await vision.FilesetResolver.forVisionTasks("/mediapipe/wasm");
      if (cancelled) return;

      landmarker = await vision.PoseLandmarker.createFromOptions(fileset, {
        baseOptions: {
          modelAssetPath: "/mediapipe/pose_landmarker_lite.task",
          delegate: "GPU",
        },
        runningMode: "VIDEO",
        numPoses: 1,
      });
      if (cancelled) {
        landmarker.close();
        landmarker = null;
        return;
      }

      frame = requestAnimationFrame(detect);
    };

    void startCamera()
      .then(() => {
        // She left while the camera was opening. Without this the detector goes
        // on to fetch its bundle for a stage that is gone.
        if (cancelled) return;
        // Nobody wants a reading: the caller is using this as a mirror, which
        // is what the camera does on a site she cannot see. Loading the model
        // to feed a callback that does not exist would spend her battery and
        // 28 MB of her connection on a result nothing reads.
        if (!onReadingRef.current) return;
        return startDetector().catch(() => {
          // Deliberately swallowed. The mirror is the part she consented to and
          // the part that works without 28 MB of WebAssembly.
          if (!cancelled) onDetectorUnavailableRef.current?.();
        });
      })
      .catch(() => {
        if (cancelled) return;
        // Only the camera reaches here now: she refused it, or there is no
        // device. The walk works without it, so the stage stands down.
        stop();
        onMountErrorRef.current();
      });

    return stop;
  }, []);

  return (
    <video
      ref={videoRef}
      autoPlay
      playsInline
      muted
      style={{
        width: "100%",
        height: "100%",
        objectFit: "cover",
        // Mirrored here, on the video alone. The diagram is a sibling element
        // and must not be flipped — a reversed drawing of where to inject is
        // worse than none.
        transform: "scaleX(-1)",
      }}
    />
  );
}
